import { format, parseISO } from 'date-fns';
import { standardCollator } from 'js/utils/string';
import { isOrgTeamRubricCustomSchemeId } from 'js/utils/orgTeamRubricScheme';
// --- Weight scheme lookup & picker (document.weight_overrides map) ---

/** Value used in `<option value>` — map key is the stable scheme id. */
export function weightSchemeOptionValue(entry) {
  return String(entry?.id ?? '').trim();
}

export function findWeightOverride(document, schemeRef) {
  const k = String(schemeRef ?? '').trim();
  if (!k) return null;
  const m = document?.weight_overrides ?? {};
  const direct = m[k];
  if (direct && typeof direct === 'object') {
    return { id: k, name: direct.name, weights: direct.weights };
  }
  for (const [id, entry] of Object.entries(m)) {
    if (entry && typeof entry === 'object') {
      const name = String(entry.name ?? '').trim();
      if (name === k) {
        return { id, name: entry.name, weights: entry.weights };
      }
    }
  }
  return null;
}

/**
 * Select value for a stored `teams_rubrics.weight_scheme` against current rubric
 * `weight_overrides`. Empty string when stored scheme no longer exists (standard weights).
 */
export function effectiveWeightSchemeSelectValue(storedScheme, document) {
  if (storedScheme == null || String(storedScheme).trim() === '') {
    return '';
  }
  if (isOrgTeamRubricCustomSchemeId(storedScheme)) {
    return storedScheme;
  }
  const o = findWeightOverride(document, storedScheme);
  return o ? weightSchemeOptionValue(o) : '';
}

/** Sorted entries for dropdowns (same order as weights UI). */
export function weightOverridesSortedForPicker(document) {
  const overrides = document?.weight_overrides ?? {};

  return Object.entries(overrides)
    .map(([id, entry]) =>
      entry && typeof entry === 'object' ? { id, ...entry } : null
    )
    .filter(Boolean)
    .sort(
      (a, b) =>
        standardCollator.compare(a.name, b.name) ||
        standardCollator.compare(a.id, b.id)
    );
}

// --- Client-side evaluation reweighting (team scores / previews) ---

export function parseWeight(weight) {
  const n = Number(weight);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function weightedMean(pairs) {
  const valid = pairs.filter(
    ([score, weight]) => score != null && Number.isFinite(score) && weight > 0
  );
  if (valid.length === 0) return null;
  const weightedTotal = valid.reduce(
    (sum, [score, weight]) => sum + score * weight,
    0
  );
  const weightTotal = valid.reduce((sum, [, weight]) => sum + weight, 0);
  return weightTotal > 0 ? weightedTotal / weightTotal : null;
}

/** guid → weight from rubric `document.question_sets` (standard rollup weights). */
function standardGuidWeightMap(document) {
  const map = {};
  const sets = document?.question_sets;
  if (!Array.isArray(sets)) {
    return map;
  }
  for (const qs of sets) {
    if (qs?.guid) {
      map[qs.guid] = qs.weight;
    }
    for (const q of qs?.questions || []) {
      if (q?.guid) {
        map[q.guid] = q.weight;
      }
    }
  }
  return map;
}

/**
 * Recomputes aggregate scores in `scoresData.evaluation` for a different weight scheme
 * without refetching. When `schemeRef` is empty, returns `scoresData` unchanged unless
 * `options.defaultWeightsFromDocument` is true (then uses weights from `document.question_sets`).
 *
 * The API scores with the team's saved scheme, so `scoresData` carries that scheme's
 * weights. Schemes store only the weights they change (a missing guid inherits the
 * document default), so weights are rebuilt from the document defaults with the
 * selected scheme's overrides on top, not layered over the saved scheme. The API's
 * own `apply_weight_scheme` works the same way.
 */
export function applyWeightSchemeToEvaluation(
  scoresData,
  document,
  schemeRef,
  options = {}
) {
  const { defaultWeightsFromDocument = false } = options;
  if (!scoresData?.evaluation) return scoresData;

  const trimmed = String(schemeRef ?? '').trim();
  if (trimmed === '' && !defaultWeightsFromDocument) {
    return scoresData;
  }

  const defaults = standardGuidWeightMap(document);
  const selected =
    trimmed !== '' ? findWeightOverride(document, trimmed) : null;
  const overrides =
    selected?.weights && typeof selected.weights === 'object'
      ? selected.weights
      : {};

  // Nodes without a guid cannot be matched to the document, so they keep the
  // weight the API scored them with.
  const resolveWeight = (guid, fallback) => {
    if (!guid) return fallback;
    return overrides[guid] != null ? overrides[guid] : defaults[guid];
  };

  const questionSets = scoresData.evaluation.question_sets || [];
  const nextQuestionSets = questionSets.map((qs) => {
    const setWeightParsed = parseWeight(resolveWeight(qs.guid, qs.weight));

    const questions = (qs.questions || []).map((q) => {
      const qWeightParsed = parseWeight(resolveWeight(q.guid, q.weight));
      // The API excludes nodes the saved scheme leaves unweighted; decide
      // again from this scheme's weight. Retired nodes stay out.
      return {
        ...q,
        weight: qWeightParsed,
        included_in_numeric:
          !q.retired && q.score != null && qWeightParsed != null,
      };
    });

    const questionPairs = questions
      .filter((q) => q.included_in_numeric)
      .map((q) => [q.score, Number(q.weight)]);
    const setScore = weightedMean(questionPairs);

    return {
      ...qs,
      weight: setWeightParsed,
      included_in_numeric:
        !qs.retired && setWeightParsed != null && setScore != null,
      questions,
      score: setScore,
    };
  });

  const setPairs = nextQuestionSets
    .filter((qs) => qs.included_in_numeric && qs.score != null)
    .map((qs) => [qs.score, Number(qs.weight)]);
  const aggregateScore = weightedMean(setPairs);

  return {
    ...scoresData,
    evaluation: {
      ...scoresData.evaluation,
      question_sets: nextQuestionSets,
      aggregate_score: aggregateScore,
    },
  };
}

export function countQuestionSetsAndQuestions(document) {
  const sets = document?.question_sets;
  if (!Array.isArray(sets)) {
    return { questionSetCount: 0, questionCount: 0 };
  }
  const questionSetCount = sets.length;
  const questionCount = sets.reduce((sum, qs) => {
    const qsQuestions = qs?.questions;
    return sum + (Array.isArray(qsQuestions) ? qsQuestions.length : 0);
  }, 0);
  return { questionSetCount, questionCount };
}

/** Standard weights from question_sets, plus each named `weight_overrides` entry. */
export function countWeightSchemes(document) {
  const namedAdditional = Object.keys(document?.weight_overrides ?? {}).length;
  return 1 + namedAdditional;
}

// --- Retirement (editor display + client-side document filtering) ---

function parseRetiredAt(value) {
  if (value == null || String(value).trim() === '') {
    return null;
  }
  const trimmed = String(value).trim();
  const d =
    trimmed.length === 10
      ? parseISO(`${trimmed}T00:00:00.000Z`)
      : parseISO(trimmed);
  if (!Number.isNaN(d.getTime())) {
    return d;
  }
  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** Earliest retirement in an ancestor chain (question set → question → source). */
function earliestRetiredAt(...nodes) {
  const dates = nodes
    .map((node) => parseRetiredAt(node?.retired_at))
    .filter(Boolean);
  if (dates.length === 0) {
    return null;
  }
  return new Date(Math.min(...dates.map((d) => d.getTime())));
}

function activeInChain(runAt, ...nodes) {
  const effective = earliestRetiredAt(...nodes);
  if (!effective) {
    return true;
  }
  return runAt.getTime() < effective.getTime();
}

function activeQuestionSet(questionSet, runAt) {
  return activeInChain(runAt, questionSet);
}

function activeQuestion(questionSet, question, runAt) {
  return activeInChain(runAt, questionSet, question);
}

function activeSource(questionSet, question, source, runAt) {
  return activeInChain(runAt, questionSet, question, source);
}

export function isRubricQuestionSetActiveAt(questionSet, asOf = new Date()) {
  const runAt = asOf instanceof Date ? asOf : new Date(asOf);
  return activeQuestionSet(questionSet, runAt);
}

export function isRubricQuestionActiveAt(
  questionSet,
  question,
  asOf = new Date()
) {
  const runAt = asOf instanceof Date ? asOf : new Date(asOf);
  return activeQuestion(questionSet, question, runAt);
}

/**
 * Active question sets, questions, and sources at `asOf` (default: now).
 * Effective retirement is the earliest date in each ancestor chain. Questions
 * with no sources are kept; otherwise only active sources are retained.
 */
export function filterRubricDocumentForTemplate(document, asOf = new Date()) {
  if (!document) {
    return document;
  }

  const runAt = asOf instanceof Date ? asOf : new Date(asOf);
  const questionSets = (document.question_sets ?? []).flatMap((qs) => {
    if (!activeQuestionSet(qs, runAt)) {
      return [];
    }

    const questions = (qs.questions ?? []).flatMap((q) => {
      if (!activeQuestion(qs, q, runAt)) {
        return [];
      }

      const sources = q.sources ?? [];
      if (sources.length === 0) {
        return [q];
      }

      const activeSources = sources.filter((src) =>
        activeSource(qs, q, src, runAt)
      );

      return [{ ...q, sources: activeSources }];
    });

    if (questions.length === 0) {
      return [];
    }

    return [{ ...qs, questions }];
  });

  return { ...document, question_sets: questionSets };
}

/**
 * Evaluation rows for current-only charts (treemap, score list). Aligns with
 * `filterRubricDocumentForTemplate` when `document` is provided.
 */
export function filterEvaluationQuestionSetsForCurrentView(
  questionSets,
  document,
  asOf = new Date()
) {
  const sets = questionSets ?? [];

  if (!document) {
    return sets
      .filter((qs) => qs.retired !== true)
      .map((qs) => ({
        ...qs,
        questions: (qs.questions ?? []).filter((q) => q.retired !== true),
      }))
      .filter((qs) => (qs.questions ?? []).length > 0);
  }

  const filteredDoc = filterRubricDocumentForTemplate(document, asOf);
  const activeSetKeys = new Set(
    (filteredDoc.question_sets ?? []).map((qs) => qs.key)
  );
  const activeQuestionKeysBySet = new Map(
    (filteredDoc.question_sets ?? []).map((qs) => [
      qs.key,
      new Set((qs.questions ?? []).map((q) => q.key)),
    ])
  );
  const activeSourceKeysByQuestion = new Map(
    (filteredDoc.question_sets ?? []).flatMap((qs) =>
      (qs.questions ?? []).map((q) => [
        `${qs.key}\0${q.key}`,
        new Set((q.sources ?? []).map((s) => s.key)),
      ])
    )
  );

  return sets
    .filter((qs) => qs.retired !== true && activeSetKeys.has(qs.key))
    .map((qs) => {
      const activeQuestionKeys =
        activeQuestionKeysBySet.get(qs.key) ?? new Set();

      const questions = (qs.questions ?? [])
        .filter((q) => q.retired !== true && activeQuestionKeys.has(q.key))
        .map((q) => {
          const sources = q.sources ?? [];
          if (sources.length === 0) {
            return q;
          }

          const activeSourceKeys =
            activeSourceKeysByQuestion.get(`${qs.key}\0${q.key}`) ?? new Set();

          return {
            ...q,
            sources: sources.filter((s) => activeSourceKeys.has(s.key)),
          };
        });

      return { ...qs, questions };
    })
    .filter((qs) => (qs.questions ?? []).length > 0);
}

/** Human-readable retirement label in the user's local timezone. */
export function formatRetiredAtLabel(retiredAt) {
  const d = parseRetiredAt(retiredAt);
  if (!d) {
    return null;
  }
  return format(d, 'PPp');
}
